/**
 * MTS (Menu Tree Structure) slice — manages the list of menu-tree items visible
 * to the user and which Business Administration (BA) is currently selected.
 *
 * Each MtsResponse can carry display tokens (tokenized string values) that are
 * resolved client-side after the tree is loaded. updateMtsTokens patches those
 * values in-place without a full re-fetch so the UI updates without a round-trip.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { updateTokensInMtsList } from '@/utils/mtsTokenUtils'
import { MTS_HEADER_ITEMS } from '@/features/it-process/application-management/mts/constants'

export interface MtsToken {
    id: string
    key: string
    tokenizedValue: string
}

export interface MtsResponse {
    id: number | string
    icon: string
    order: number
    name: string
    businessAdministrationId: number
    tokens?: MtsToken[]
}

export type MtsState = {
    mtsList: MtsResponse[]
    selectedBA: number
    selectedBaName?: string
}

const initialState: MtsState = {
    // Hardcoded client-side items (previously fetched from `api/screen/mts`).
    mtsList: MTS_HEADER_ITEMS.map((item) => ({ ...item })),
    selectedBA: 0,
    selectedBaName: '',
}

export const mtsSlice = createSlice({
    name: 'mts',
    initialState,
    reducers: {
        setSelectedBA: (state, action: PayloadAction<number>) => {
            state.selectedBA = action.payload
        },
        setSelectedBaName: (state, action: PayloadAction<string>) => {
            state.selectedBaName = action.payload
        },
        updateMtsTokens: (
            state,
            action: PayloadAction<
                { id: string; key: string; tokenizedValue: string }[]
            >,
        ) => {
            const tokenUpdates = action.payload;
            state.mtsList = updateTokensInMtsList(state.mtsList, tokenUpdates);
        },
        resetMtsState: (state) => {
            // Restore pristine hardcoded items (drops any per-language tooltip
            // tokens applied for the previous user/session).
            state.mtsList = MTS_HEADER_ITEMS.map((item) => ({ ...item }))
            state.selectedBA = 0
            state.selectedBaName = ''
        },
    },
})

export const {
    setSelectedBA,
    setSelectedBaName,
    updateMtsTokens,
    resetMtsState,
} = mtsSlice.actions

export default mtsSlice.reducer
